import { buildDeck } from './cards';
import { reinforcementsFor } from './helpers';
import { createRng, pick, shuffle } from './rng';
import { rulesFor } from './rules';
import type { GameConfig, GameState, MapDef, Owner, Territory, TerritoryId } from './types';

const COLORS = new Set(['cyan', 'magenta', 'amber', 'violet']);

export function validateConfig(map: MapDef, config: GameConfig): string | null {
  if (config.mapId !== map.id) return `Config is for map ${config.mapId}, got ${map.id}`;
  const n = config.players.length;
  if (n < 1 || n > 4) return 'Need 1 to 4 players';
  if (new Set(config.players.map((p) => p.id)).size !== n) return 'Player ids must be unique';
  if (new Set(config.players.map((p) => p.color)).size !== n)
    return 'Player colours must be unique';
  for (const p of config.players) if (!COLORS.has(p.color)) return `Unknown colour ${p.color}`;
  if (config.players.some((p) => p.id === 'horde')) return "'horde' is a reserved id";
  if (!Number.isInteger(config.seed)) return 'Seed must be an integer';
  return null;
}

/**
 * Deal the board. Seats (players plus the horde) take turns placing one unit:
 * first claiming unclaimed territories, then stacking on their own.
 */
export function createGame(map: MapDef, config: GameConfig): GameState {
  const problem = validateConfig(map, config);
  if (problem) throw new Error(problem);

  const rules = rulesFor(config.difficulty);
  const n = config.players.length as 1 | 2 | 3 | 4;
  let rng = createRng(config.seed);

  const territories: Record<TerritoryId, Territory> = {};
  for (const t of map.territories)
    territories[t.id] = { id: t.id, owner: null, armies: 0, zombies: 0 };

  const seats: Owner[] = [...config.players.map((p) => p.id), 'horde'];
  const perPlayer = rules.startingUnits[n];
  const hordeUnits = Math.round(perPlayer * (n === 1 ? rules.hordeShareSolo : rules.hordeShare));
  const budget = new Map<Owner, number>(
    seats.map((s) => [s, s === 'horde' ? hordeUnits : perPlayer]),
  );
  const owned = new Map<Owner, TerritoryId[]>(seats.map((s) => [s, []]));

  let unclaimed: TerritoryId[];
  [unclaimed, rng] = shuffle(
    rng,
    map.territories.map((t) => t.id),
  );

  while ([...budget.values()].some((b) => b > 0)) {
    for (const seat of seats) {
      const left = budget.get(seat)!;
      if (left <= 0) continue;
      let id: TerritoryId;
      // The horde claims in thicker stacks so its footholds are not free captures.
      const spend = seat === 'horde' ? Math.min(left, rules.hordeStack) : 1;
      if (unclaimed.length > 0) {
        id = unclaimed.pop()!;
        territories[id]!.owner = seat;
        owned.get(seat)!.push(id);
      } else {
        [id, rng] = pick(rng, owned.get(seat)!);
      }
      const t = territories[id]!;
      if (seat === 'horde') t.zombies += spend;
      else t.armies += 1;
      budget.set(seat, left - spend);
    }
  }

  let deck: string[];
  [deck, rng] = shuffle(rng, buildDeck());

  const objectives = n === 1 ? { eradication: true } : config.objectives;

  const state: GameState = {
    config: { ...config, objectives },
    round: 1,
    phase: 'reinforce',
    currentPlayerIndex: 0,
    players: config.players.map((p) => ({ ...p, hand: [], eliminated: false })),
    territories,
    deck,
    discard: [],
    rng,
    reinforcementsPending: 0,
    drawsPending: 0,
    pendingOccupy: null,
    fortified: false,
    modifiers: {
      quarantineUntilRound: 0,
      mutationSteps: 0,
      mutationUntilRound: 0,
      sirenPlayers: [],
    },
    outcome: null,
  };
  state.reinforcementsPending = reinforcementsFor(state, map, config.players[0]!.id);
  return state;
}
